import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Cookie } from 'lucide-react';

type CookiePreferences = {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showCookieIcon, setShowCookieIcon] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    advertising: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('cookieChoice_necessary');
    
    if (!hasConsent) {
      // First visit - show banner after short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      setPreferences({
        necessary: true, // Always true
        analytics: localStorage.getItem('cookieChoice_analytics') === 'true',
        advertising: localStorage.getItem('cookieChoice_advertising') === 'true',
      });
      setShowCookieIcon(true);
      
      // Update consent mode with saved preferences
      updateConsentMode({
        necessary: true,
        analytics: localStorage.getItem('cookieChoice_analytics') === 'true',
        advertising: localStorage.getItem('cookieChoice_advertising') === 'true',
      });
    }
  }, []);

  const updateConsentMode = (prefs: CookiePreferences) => {
    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('consent', 'update', {
        analytics_storage: prefs.analytics ? 'granted' : 'denied',
        ad_storage: prefs.advertising ? 'granted' : 'denied',
        ad_user_data: prefs.advertising ? 'granted' : 'denied',
        ad_personalization: prefs.advertising ? 'granted' : 'denied',
      });

      // Push events to dataLayer
      if ((window as any).dataLayer) {
        if (prefs.analytics) {
          (window as any).dataLayer.push({ event: 'consent_accepted_analytical' });
        }
        if (prefs.advertising) {
          (window as any).dataLayer.push({ event: 'consent_accepted_advertising' });
        }
      }
    }
  };

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookieChoice_necessary', 'true');
    localStorage.setItem('cookieChoice_analytics', prefs.analytics.toString());
    localStorage.setItem('cookieChoice_advertising', prefs.advertising.toString());
    
    setPreferences(prefs);
    updateConsentMode(prefs);
    setShowBanner(false);
    setShowPreferences(false);
    setShowCookieIcon(true);
  };

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      advertising: true,
    });
  };

  const handleRejectAll = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      advertising: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const handleManagePreferences = () => {
    setShowBanner(false);
    setShowPreferences(true);
  };

  const handleReopenPreferences = () => {
    setShowPreferences(true);
  };

  if (!showBanner && !showCookieIcon) return null;

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg animate-in slide-in-from-bottom-5 duration-500"
          role="dialog"
          aria-labelledby="cookie-banner-title"
          aria-describedby="cookie-banner-description"
        >
          <div className="container max-w-7xl mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h2 id="cookie-banner-title" className="text-lg font-semibold mb-2">
                  Cookie Preferences
                </h2>
                <p id="cookie-banner-description" className="text-sm text-muted-foreground">
                  <strong>We use cookies to enhance your user experience, provide personalized content, and analyze our traffic. 
                  You can customize your preferences or accept all cookies.</strong>{' '}
                  <a 
                    href="/privacy-policy" 
                    className="text-brand-blue hover:underline underline font-bold"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Button
                  variant="outline"
                  onClick={handleRejectAll}
                  className="w-full sm:w-auto"
                >
                  Reject Non-Essential
                </Button>
                <Button
                  variant="outline"
                  onClick={handleManagePreferences}
                  className="w-full sm:w-auto"
                >
                  Manage Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto"
                >
                  Accept All
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Dialog */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Customize Cookie Preferences</DialogTitle>
            <DialogDescription>
              We respect your privacy. Choose which cookies you want to allow.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="necessary" className="text-base font-medium">
                  Necessary Cookies
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  These cookies are essential for the website to function properly and cannot be disabled.
                </p>
              </div>
              <Switch
                id="necessary"
                checked={true}
                disabled
                aria-label="Necessary cookies (always enabled)"
              />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="analytics" className="text-base font-medium">
                  Analytics Cookies
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Help us improve the site by tracking which pages are most popular and how visitors move around.
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, analytics: checked })
                }
                aria-label="Analytics cookies"
              />
            </div>

            {/* Advertising Cookies */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label htmlFor="advertising" className="text-base font-medium">
                  Advertising Cookies
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Provide extra features and personalization to improve your experience.
                </p>
              </div>
              <Switch
                id="advertising"
                checked={preferences.advertising}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, advertising: checked })
                }
                aria-label="Advertising cookies"
              />
            </div>
          </div>

          <DialogFooter>
            <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
              Save Preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating Cookie Icon */}
      {showCookieIcon && !showBanner && (
        <button
          onClick={handleReopenPreferences}
          className="fixed bottom-6 left-6 z-40 p-3 bg-brand-blue text-white rounded-full shadow-lg hover:bg-brand-blue/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2"
          aria-label="Manage cookie preferences"
        >
          <Cookie className="w-6 h-6" />
        </button>
      )}
    </>
  );
};

export default CookieConsent;
